import { Timestamp } from 'firebase/firestore';

import type { Theme } from '@mui/material/styles';

import dayjs from 'dayjs';

import Competition, {
  COMP_ENTRIES,
  COMP_FIELDS,
  COMP_FORMATS,
  COMP_SCORING_METHODS,
  COMP_SCORINGS,
  COMPETITION_PRESETS,
} from '../types/Competition';
import EventDay from '../types/EventDay';

import { selectedDayIndices } from './utilsEvents';

/** Readable one-line phrase describing a competition, e.g. "Team pairs · NET · vs. Flight".
 *  `hasLeagueIndexBrackets` is passed when this competition rolls up into a League competition
 *  with LeagueCompetition.indexBrackets set — it's read purely for display here, never persisted
 *  onto the competition's own `field`/`indexBrackets` (see computeAndWriteResults.ts's own comment
 *  for why: keeping the stored competition untouched means its own editor never shows a phantom
 *  Field=INDEX the admin never set on THIS competition — but that same choice means this label
 *  would otherwise show "vs. Field" even though the competition is actually bracket-scoped). */
export function describeCompetition(competition: Competition, hasLeagueIndexBrackets?: boolean): string {
  const { scoring, entry, format, handicap, field, holes, teamRollup } = competition;

  if (scoring === COMP_SCORINGS.CLOSEST_TO_PIN) {
    const holeList = holes?.length ? ` · Holes ${holes.join(', ')}` : '';
    return `Closest to pin${holeList}`;
  }
  if (scoring === COMP_SCORINGS.LONG_DRIVE) {
    const holeNum = holes?.length ? ` · Hole ${holes[0]}` : '';
    return `Long drive${holeNum}`;
  }

  const parts: string[] = [];

  const entryPhrase = buildEntryPhrase(entry, format, !!teamRollup);
  if (entryPhrase) parts.push(entryPhrase);

  if (handicap) parts.push(handicap);

  const scoringPhrase = buildScoringPhrase(scoring);
  if (scoringPhrase) parts.push(scoringPhrase);

  const fieldPhrase = buildFieldPhrase(field, scoring, hasLeagueIndexBrackets, entry);
  if (fieldPhrase) parts.push(fieldPhrase);

  return parts.join(' · ');
}

function buildEntryPhrase(
  entry: Competition['entry'],
  format: Competition['format'],
  isTeam?: boolean,
): string {
  const teamPrefix = isTeam && entry !== COMP_ENTRIES.TEAM ? 'Team ' : '';
  switch (entry) {
    case COMP_ENTRIES.SINGLE:
      return isTeam ? 'Team individuals' : 'Individual';
    case COMP_ENTRIES.PAIR:
      if (format === COMP_FORMATS.BESTBALL) return `${teamPrefix}best-ball pairs`;
      if (format === COMP_FORMATS.ALTERNATE_SHOT) return `${teamPrefix}alternate shot pairs`;
      if (format === COMP_FORMATS.CHAPMAN) return `${teamPrefix}Chapman pairs`;
      if (format === COMP_FORMATS.SHAMBLE) return `${teamPrefix}shamble pairs`;
      return `${teamPrefix}pairs`;
    case COMP_ENTRIES.GROUP:
      if (format === COMP_FORMATS.SCRAMBLE) return `${teamPrefix}scramble`;
      if (format === COMP_FORMATS.SHAMBLE) return `${teamPrefix}shamble`;
      if (format === COMP_FORMATS.BESTBALL) return `${teamPrefix}best-ball`;
      if (format === COMP_FORMATS.ALTERNATE_SHOT) return `${teamPrefix}alternate shot`;
      return `${teamPrefix}groups`;
    case COMP_ENTRIES.TEAM:
      return 'Team';
    case COMP_ENTRIES.FOURSOME:
      return `${teamPrefix}foursomes`;
    case COMP_ENTRIES.WHEEL:
      return 'Wheel';
    case COMP_ENTRIES.FOUR_COUPLES:
    case COMP_ENTRIES.TWO_COUPLES:
      return 'Couples';
    case COMP_ENTRIES.CLUB:
      return 'Club';
    default:
      return '';
  }
}

/** Shared "scoring → label" source of truth, e.g. for the CompetitionCard Game chip and this file's own phrase-building.
 *  Skins variants defer to COMPETITION_PRESETS' own curated names — those already spell out what
 *  distinguishes them (e.g. "Skins (carryover)" vs. plain "Skins") rather than relying on names
 *  ("Basic"/"Canadian"/"Silk") that don't mean anything to a player on their own. */
export function buildScoringPhrase(scoring: Competition['scoring']): string {
  switch (scoring) {
    case COMP_SCORINGS.STABLEFORD: return 'Stableford';
    case COMP_SCORINGS.SKINS_BASIC:
    case COMP_SCORINGS.SKINS_CANADIAN:
    case COMP_SCORINGS.SKINS_SILK:
      return COMPETITION_PRESETS[scoring]?.name ?? 'Skins';
    case COMP_SCORINGS.MATCH: return 'Match play';
    case COMP_SCORINGS.MATCH_FULL_ROUND: return 'Match play';
    case COMP_SCORINGS.NASSAU: return 'Nassau';
    case COMP_SCORINGS.NASSAU_9_HOLES: return 'Nassau';
    case COMP_SCORINGS.VEGAS: return 'Vegas';
    case COMP_SCORINGS.COUNT: return 'Count';
    default: return '';
  }
}

/** Playing-unit noun for Vs labels once the unit isn't a plain individual — e.g. so "vs. Field"
 *  can read as "vs. All Pairs" once entry is a pair. entry=PAIR already IS pairs-ranked-against-
 *  pairs today (each pair is its own scoring entry) — this is a labeling fix, not a new mechanism;
 *  the confusion this fixes is that "Field"/"Flight"/"Division" read as if they mean individuals. */
export function vsUnitNoun(entry: Competition['entry']): string | null {
  if (entry === COMP_ENTRIES.PAIR) return 'Pairs';
  if (entry === COMP_ENTRIES.FOURSOME) return 'Foursomes';
  if (entry === COMP_ENTRIES.GROUP) return 'Groups';
  return null;
}

function buildFieldPhrase(
  field: Competition['field'],
  scoring: Competition['scoring'],
  hasLeagueIndexBrackets?: boolean,
  entry?: Competition['entry'],
): string {
  // Match scoring already implies head-to-head — field phrase is redundant
  if (scoring === COMP_SCORINGS.MATCH || scoring === COMP_SCORINGS.MATCH_FULL_ROUND) return '';
  // An unset/ALL field with no explicit scoping of its own inherits the linked League
  // competition's brackets at compute time (see computeAndWriteResults.ts) — reflect that here too.
  const effectiveField = (!field || field === COMP_FIELDS.ALL) && hasLeagueIndexBrackets
    ? COMP_FIELDS.INDEX
    : field;
  const unit = vsUnitNoun(entry);
  switch (effectiveField) {
    case COMP_FIELDS.ALL: return unit ? `vs. All ${unit}` : 'vs. Field';
    case COMP_FIELDS.FLIGHT: return unit ? `vs. ${unit} in Flight` : 'vs. Flight';
    case COMP_FIELDS.DIVISION: return unit ? `vs. ${unit} in Division` : 'vs. Division';
    case COMP_FIELDS.PHYSICAL_GROUP: return unit ? `vs. ${unit} in Group` : 'vs. Pairing Group';
    case COMP_FIELDS.FOURSOME: return 'vs. Foursome';
    case COMP_FIELDS.PAIR: return 'vs. Pair';
    case COMP_FIELDS.INDEX: return 'by Index';
    default: return '';
  }
}

/** CSS color for a competition's left colorbar, keyed to scoring category */
export function getCompetitionColorbar(competition: Competition, palette: Theme['palette']): string {
  switch (competition.scoring) {
    case COMP_SCORINGS.STROKE:
    case COMP_SCORINGS.STABLEFORD:
    case COMP_SCORINGS.COUNT:
      return palette.primary.main;
    case COMP_SCORINGS.SKINS_BASIC:
    case COMP_SCORINGS.SKINS_CANADIAN:
    case COMP_SCORINGS.SKINS_SILK:
      return palette.warning.main;
    case COMP_SCORINGS.MATCH:
    case COMP_SCORINGS.MATCH_FULL_ROUND:
    case COMP_SCORINGS.NASSAU:
    case COMP_SCORINGS.NASSAU_9_HOLES:
      return palette.error.main;
    case COMP_SCORINGS.VEGAS:
      return palette.secondary.main;
    case COMP_SCORINGS.CLOSEST_TO_PIN:
    case COMP_SCORINGS.LONG_DRIVE:
      return palette.success.main;
    default:
      return palette.text.disabled;
  }
}

/** Days summary string for a competition in a multi-day event. Returns null for single-day events. */
export function buildCompetitionDaysSummary(comp: Competition, days: EventDay[]): string | null {
  if (days.length <= 1) return null;
  const indices = selectedDayIndices(comp, days);
  if (!indices || indices.length === days.length) return 'All days';
  if (indices.length === 0) return 'No days';

  const dateStrs = days.map(d => {
    const ts = d.datetime;
    return ts instanceof Timestamp ? dayjs(ts.toDate()).format('YYYY-MM-DD') : '';
  });
  const hasDuplicate = dateStrs.some((s, i) => s && dateStrs.indexOf(s) !== i);

  return indices
    .map(i => {
      const ts = days[i]?.datetime;
      if (!(ts instanceof Timestamp)) return '';
      const d = dayjs(ts.toDate());
      return hasDuplicate ? d.format('M/D/YY h:mm A') : d.format('M/D/YY');
    })
    .filter(Boolean)
    .join(', ');
}

/**
 * True when the day includes an individual (entry: SINGLE) match play competition -- the one
 * shape where the results engine splits a physical group into independent 1v1 matchups by
 * adjacent pairs (p1 vs p2, p3 vs p4 -- MATCHUP_SPLIT_STRATEGIES.CHUNKED_PAIRS, unitSize 1; see
 * computeMatchEntries's isSingleEntry branch) rather than a PAIR competition's "whole group split
 * in half." The printed scorecard uses this both to group each matchup's two player rows tight
 * (see exportEventScorecardPdf.ts) and to decide whether a "one card per match" print option is
 * even offered (see EventPrinter.tsx) -- kept here, not in the exporter itself, since the exporter
 * is dynamically imported for bundle-splitting (it pulls in jsPDF) and the print options panel
 * needs this same check before the user has even chosen to print.
 */
export function hasSingleEntryMatchPlay(competitions: Competition[]): boolean {
  return competitions.some(c =>
    c.entry === COMP_ENTRIES.SINGLE &&
    c.scoringMethod === COMP_SCORING_METHODS.MATCH_WINS &&
    !c.scorecardConfig?.hideFromScorecard,
  );
}
